import { defineSlotRecipe } from '@chakra-ui/react';
import { Borders, Colors, FontSizes, Radii, Shadows, Space, BorderWidths, TextStyles } from '../../types';

const controlBase = {
  boxSize: '1.125em',
  lineHeight: 'inherit',
  my: 'px',
  borderStyle: 'solid',
  fontSize: 'inherit',
  borderRadius: Radii.SM,
  borderWidth: BorderWidths.XS,
  borderColor: Colors.BORDER_INPUT,
  background: Colors.SURFACE_PAGE,
  '& svg': {
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    boxSize: '1em',
    color: Colors.TEXT_ON_PRIMARY,
  },
  _hover: {
    borderColor: Colors.BORDER_EMPHASIS,
    bg: Colors.SURFACE_PAGE,
  },
  _indeterminate: {
    background: Colors.SURFACE_PRIMARY,
    borderColor: Colors.SURFACE_PRIMARY_ACTIVE,
  },
  _checked: {
    background: Colors.SURFACE_BRAND,
    borderColor: Colors.BORDER_SUBDUED,
    color: Colors.TEXT_ON_PRIMARY,
    _hover: {
      bg: Colors.SURFACE_PRIMARY_HOVER,
      borderColor: Colors.BORDER_SUBDUED,
    },
    _disabled: {
      bg: Colors.SURFACE_DISABLED,
      color: Colors.TEXT_DISABLED,
    },
    _invalid: {
      bg: Colors.SURFACE_ERROR,
      borderColor: Colors.BORDER_ERROR,
    },
  },
  _disabled: {
    bg: Colors.SURFACE_DISABLED,
    color: Colors.TEXT_DISABLED,
  },
  _invalid: {
    bg: Colors.SURFACE_ERROR_SUBDUED,
    borderColor: Colors.BORDER_ERROR,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: Colors.BORDER_FOCUS,
    outlineOffset: '1px',
    boxShadow: 'none',
  },
};

export const checkboxSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'control', 'indicator', 'group', 'hiddenInput'],
  base: {
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: Space.XS,
    },
    root: {
      transitionProperty: 'common',
      transitionDuration: 'normal',
      display: 'flex',
      alignItems: 'start',
      gap: Space.XS,
    },
    label: {
      color: Colors.TEXT_MUTED,
      textStyle: TextStyles.UI_DEFAULT,
      m: 0,
      alignItems: 'baseline',
      _checked: {
        color: Colors.TEXT_EMPHASIS,
      },
    },
    control: controlBase,
  },
  variants: {
    variant: {
      // Chakra's checkbox defaults to variant `solid`, which beats `base.control`, so `solid` restates it. The
      // built-in's literal `&:is(...)` checked rule stays separate from `_checked` and wins on order, so it's restated.
      solid: {
        control: {
          ...controlBase,
          '&:is([data-state=checked], [data-state=indeterminate])': {
            background: Colors.SURFACE_BRAND,
            borderColor: Colors.BORDER_SUBDUED,
            color: Colors.TEXT_ON_PRIMARY,
          },
        },
      },
      card: {
        root: {
          py: Space.XS,
          px: Space.SM,
          pe: Space.XS,
          bg: Colors.SURFACE_PAGE,
          borderWidth: Borders['1PX'],
          borderStyle: 'solid',
          borderColor: Colors.BORDER_DEFAULT,
          rounded: Radii.LG,
          position: 'relative',
          me: Space.XS,
          gap: Space.XS,
          w: 'full',
          h: 'full',
          _hover: {
            borderColor: Colors.BORDER_INPUT,
            bg: Colors.SURFACE_HOVER,
          },
          _checked: {
            bg: Colors.SURFACE_SELECTED,
            borderColor: Colors.BORDER_BRAND,
          },
          '&:has(input:focus-visible)': {
            outline: '2px solid',
            outlineColor: Colors.BORDER_FOCUS,
            outlineOffset: '1px',
            boxShadow: 'none',
          },
        },
        label: {
          flex: '1',
          alignSelf: 'center',
        },
        control: {
          order: '2',
          _focus: {
            boxShadow: Shadows.NONE,
            outline: 'none',
          },
        },
      },
      stacked: {
        control: {
          justifySelf: 'center',
          margin: '0 auto',
        },
        label: {
          w: 'auto',
          textAlign: 'center',
        },
        root: {
          flexDirection: 'column',
          display: 'inline-flex',
          alignItems: 'center',
          flex: '1',
        },
      },
    },
    size: {
      lg: {
        control: { boxSize: 6 },
        label: {
          '& *:not(:where(.chakra-field__helperText))': { textStyle: TextStyles.UI_DEFAULT, fontWeight: 'normal' },
        },
      },
      md: {
        control: { boxSize: 4, mt: 0.5 },
        label: {
          '& *:not(:where(.chakra-field__helperText))': { textStyle: TextStyles.UI_DEFAULT, fontWeight: 'normal' },
        },
      },
      sm: {
        control: { boxSize: 4 },
        label: { fontSize: FontSizes.SM },
      },
      xs: {
        control: { boxSize: 3.5 },
        label: { fontSize: FontSizes.XS },
      },
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'solid',
  },
});
